<template>
  <div class="mobile-pos-container">
    <vue-drawer-layout 
      ref="drawer" 
      :drawer-width="320" 
      :enable="true" 
      :backdrop="true" 
      @mask-click="closeDrawer"
      @slide-end="onDrawerSlideEnd"
    >
      <div slot="drawer">
        <NavigationDrawer :close="() => closeDrawer()" class="navigation-drawer" />
      </div>

      <div slot="content" class="mobile-pos-content">
        <!-- Minimal Mobile Header -->
        <div class="mobile-top-bar">
          <div class="header-left">
            <button @click="openDrawer" class="menu-btn">
              <font-awesome-icon icon="fa-solid fa-bars" />
            </button>
          </div>
          <div class="header-search">
             <input 
                type="text" 
                v-model="searchInput"
                @input="onSearchInput"
                class="search-input" 
                placeholder="Search products..."
              >
              <button class="clear-btn" @click="clearSearch" v-if="searchInput">
                <font-awesome-icon icon="fa-solid fa-times" />
              </button>
          </div>
          <div class="header-right">
            <InternetOnOff />
          </div>
        </div>

        <transition name="slide-down">
          <div v-if="showChangeNotification" class="change-notification-banner">
            <div class="change-content">
              <div class="change-header">
                <div class="change-label">CHANGE DUE</div>
                <div v-if="lastSaleInvoice" class="invoice-label">Invoice: {{ lastSaleInvoice }}</div>
              </div>
              <div class="change-amount">
                {{ $utils.formatMoney(lastSaleChange, 2, '.', ',', true) }}
              </div>
              <button class="dismiss-btn" @click="dismissChange">OK</button>
            </div>
          </div>
        </transition>
        
        <!-- Category Tabs (Sticky) -->
        <MobileCategoryTabs />
        
        <!-- Main Content Area -->
        <div class="mobile-main-content">
          <!-- Open Table Alert -->
          <div v-if="$store.getters.getOpenTableName" class="open-table-banner" @click="viewOpenTable">
            <font-awesome-icon icon="fa-solid fa-utensils" />
            <span>Open Table: {{ $store.getters.getOpenTableName }}</span>
          </div>

          <!-- Product Grid (Scrollable) -->
          <div class="products-container" ref="productsContainer">
            <MobileProductGrid @add-to-cart="handleAddToCart" />
          </div>
        </div>

        <!-- Bottom Navigation Bar -->
        <MobileBottomNav 
          :cart-count="cartItemCount"
          :cart-total="cartTotal"
          @open-cart="openCart"
          @navigate-tables="navigateToTables"
          @navigate-payment="navigateToPayment"
        />

        <!-- Full-Screen Cart View -->
        <MobileCartView 
          v-if="showCart"
          @close="closeCart"
          @proceed-to-payment="proceedToPayment"
          @save-table="saveTable"
        />
      </div>
    </vue-drawer-layout>
  </div>
</template>

<script>
import NavigationDrawer from "@/components/layout/navigation/NavigationDrawer.vue";
import InternetOnOff from "@/components/layout/NetworkChecker.vue";
import MobileCategoryTabs from "@/components/point-of-sale/sales/pos/MobileCategoryTabs.vue";
import MobileProductGrid from "@/components/point-of-sale/sales/pos/MobileProductGrid.vue";
import MobileBottomNav from "@/components/point-of-sale/sales/pos/MobileBottomNav.vue";
import MobileCartView from "@/components/point-of-sale/sales/pos/MobileCartView.vue";
import { responses } from "@/utils/responses";

export default {
  name: "MobilePOSView",
  components: {
    NavigationDrawer,
    InternetOnOff,
    MobileCategoryTabs,
    MobileProductGrid,
    MobileBottomNav,
    MobileCartView
  },
  data() {
    return {
      searchInput: '',
      searchTimer: null,
      showCart: false,
      saleNumber: this.$route.query.saleNumber,
      dontReload: this.$route.query.dontReload,
      isDrawerOpen: false,
      isNavigatingToTables: false,
    };
  },
  computed: {
    cartItemCount() {
      return this.$store.getters.getSaleLines?.length || 0;
    },
    cartTotal() {
      const saleLines = this.$store.getters.getSaleLines || [];
      return saleLines.reduce((total, line) => total + (line.Line || 0), 0);
    },
    lastSaleChange() {
      return this.$store.getters.getLastSaleChange;
    },
    lastSaleInvoice() {
      return this.$store.getters.getLastSaleInvoice;
    },
    showChangeNotification() {
      return this.$store.getters.getShowChangeNotification;
    }
  },
  beforeMount() {
    this.$store.dispatch('resetTableSavingState');
    this.getTableBySaleNumber();
  },
  mounted() {
    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.handleDocumentClick, true);
    }
  },
  beforeDestroy() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.handleDocumentClick, true);
    }
  },
  watch: {
    cartItemCount(newVal, oldVal) {
      if (this.showChangeNotification && oldVal !== undefined && newVal > oldVal) {
        this.dismissChange();
      }
    }
  },
  methods: {
    dismissChange() {
      this.$store.commit('clearLastSaleChange');
    },
    /**
     * Opens the nav drawer when the ref is mounted.
     * Sentry RESTAURANT-POS-5/B/J: avoid TypeError when drawer is not mounted.
     */
    openDrawer() {
      const drawer = this.$refs.drawer;
      if (drawer && typeof drawer.toggle === 'function') {
        drawer.toggle();
      }
      this.isDrawerOpen = true;
    },
    /**
     * Closes the nav drawer when the ref is mounted.
     * Sentry RESTAURANT-POS-5/B/J: avoid TypeError when drawer is not mounted.
     */
    closeDrawer() {
      const drawer = this.$refs.drawer;
      if (drawer && typeof drawer.toggle === 'function') {
        drawer.toggle(false);
      }
      this.isDrawerOpen = false;
    },
    onDrawerSlideEnd(visible) {
      this.isDrawerOpen = visible;
    },
    handleDocumentClick(event) {
      if (!this.isDrawerOpen) return;
      const drawerRoot = this.$refs.drawer?.$el;
      if (!drawerRoot) return;
      const drawerWrap = drawerRoot.querySelector('.drawer-wrap');
      if (drawerWrap && drawerWrap.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeDrawer();
    },
    onSearchInput() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      
      this.searchTimer = setTimeout(() => {
        this.searchProducts();
      }, 300);
    },
    searchProducts() {
      const categorySearch = this.$store.getters.getSearchCategories;
      
      const query = {
        description: this.searchInput,
        category: categorySearch.category || '',
        subCategory: categorySearch.subCategory || '',
        sub2Category: categorySearch.sub2Category || ''
      };
      
      this.$store.dispatch('searchDescription', query);
    },
    clearSearch() {
      this.searchInput = '';
      this.searchProducts();
    },
    handleAddToCart(product) {
      // Product added - could show toast notification
      console.log('Product added:', product);
    },
    openCart() {
      this.showCart = true;
    },
    closeCart() {
      this.showCart = false;
    },
    viewOpenTable() {
      this.$router.push({ name: 'tables' });
    },
    navigateToTables() {
      this.$router.push({ name: 'tables' });
    },
    navigateToPayment() {
      if (this.cartItemCount === 0) {
        return responses.showWarning('Cart is empty');
      }
      this.$router.push({ name: 'payment' });
    },
    proceedToPayment() {
      this.closeCart();
      this.navigateToPayment();
    },
    async saveTable() {
      // Prevent duplicate calls if already saving
      if (this.$store.state.tableModule.isSavingTable) {
        return;
      }

      const existingTableName = this.$store.getters.getOpenTableName;
      
      if (existingTableName) {
        this.$utils.showLoading();
        try {
          const updateResult = await this.$store.dispatch('updateTable', {
            name: existingTableName,
            Customer: this.$store.getters.getCustomer,
          });
          // RESTAURANT-POS-Q: soft-skip — first save still in flight; stay put
          if (updateResult && updateResult.skipped) {
            console.warn('[MobilePOSView] updateTable soft-skipped (already saving)');
            return;
          }
          
          await this.$store.dispatch("clearLocalStorage");
          this.$store.commit('clearSale');
          this.$store.commit('setOpenTableName', ''); // Ensure name is cleared
          
          responses.showSuccess('Table saved successfully');
          this.closeCart();
          this.isNavigatingToTables = true;
          await this.$store.dispatch('proceedToSales');
        } catch (error) {
          const msg = error.message || error;
          responses.showError('Failed to save table: ' + msg);
        } finally {
          this.$utils.hideLoading();
        }
      } else {
        // Prompt for table name
        this.$store.commit('setIsTabOpen', true);
        
        const result = await Swal.fire({
          title: 'Table Name',
          icon: 'question',
          input: 'text',
          showCancelButton: true,
          confirmButtonText: 'Save Table',
          allowOutsideClick: false,
          preConfirm: (input) => {
            if (!input) return Swal.showValidationMessage('Table name is required');
            return input;
          }
        });
        
        this.$store.commit('setIsTabOpen', false);
        
        if (result.isConfirmed) {
          this.$utils.showLoading();
          try {
            this.$store.commit('setOpenTableName', result.value);
            const saveResult = await this.$store.dispatch('saveNewTable', {
              name: result.value,
              Customer: this.$store.getters.getCustomer,
            });
            // RESTAURANT-POS-Q: soft-skip — first save still in flight; stay put
            if (saveResult && saveResult.skipped) {
              console.warn('[MobilePOSView] saveNewTable soft-skipped (already saving)');
              return;
            }
            
            await this.$store.dispatch("clearLocalStorage");
            this.$store.commit('clearSale');
             this.$store.commit('setOpenTableName', ''); // Ensure name is cleared
            
            responses.showSuccess(`Table ${result.value} saved successfully`);
            this.closeCart();
            this.isNavigatingToTables = true;
            await this.$store.dispatch('proceedToSales');
          } catch (error) {
            const msg = error.message || error;
            responses.showError('Failed to save table: ' + msg);
          } finally {
            this.$utils.hideLoading();
          }
        }
      }
    },
    async getTableBySaleNumber() {
      if (this.dontReload || !this.saleNumber) {
        await this.$store.dispatch('loadFromLocalStorage');
        return;
      }

      this.$utils.showLoading();
      try {
        await this.$store.dispatch('getTableBySaleNumber', this.saleNumber);
      } catch (error) {
        // Error already handled by store, but we catch here to prevent unhandled promise rejection
        console.warn('[MobilePOSView] Table loading error:', error);
      } finally {
        this.$utils.hideLoading();
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.isNavigatingToTables) return next();

    // Same logic as desktop POSView for consistency
    if (this.$store.getters.getSaleLines.length === 0) return next();
    
    if (to.name === 'instruction' || to.name === 'payment' || to.name === 'select-customer') {
      return next();
    }
    
    if (to.name === 'tables') {
      const existingTableName = this.$store.getters.getOpenTableName;
      
      if (existingTableName) {
        this.$utils.showLoading();
        return this.$store.dispatch('updateTable', {
          name: existingTableName,
          Customer: this.$store.getters.getCustomer,
        }).then((result) => {
          // RESTAURANT-POS-Q: soft-skip — do not clear cart or leave while first save runs
          if (result && result.skipped) {
            this.$utils.hideLoading();
            next(false);
            return;
          }
          this.$store.dispatch("clearLocalStorage");
          this.$store.commit('clearSale');
          this.$utils.hideLoading();
          this.isNavigatingToTables = true;
          next(false);
          return this.$store.dispatch('proceedToSales');
        }).catch(error => {
          this.$utils.hideLoading();
          responses.showError('Failed to save table: ' + (error.message || error));
        });
      }
    } else {
      return responses.showWarning('Save or Use Current Table');
    }
  }
};
</script>

<style scoped lang="scss">
.mobile-pos-container {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background: var(--bg-gradient);
  position: fixed;
  top: 0;
  left: 0;
}

.mobile-pos-content {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg-primary);
  overflow: hidden;
}

.mobile-top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 12px;
  background: var(--bg-primary);
  border-bottom: var(--card-border);
  position: sticky;
  top: 0;
  z-index: 1000;
  
  .menu-btn {
    width: 40px;
    height: 40px;
    border-radius: var(--button-radius);
    background: transparent;
    color: var(--text-primary);
    border: none;
    font-size: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: var(--transition-default);
    
    &:active {
      background: var(--secondary-button-bg);
    }
  }
}

.header-search {
  flex: 1;
  margin: 0 12px;
  position: relative;
  display: flex;
  align-items: center;

  .search-input {
    width: 100%;
    height: 40px;
    padding: 0 40px 0 16px;
    border: var(--card-border);
    border-radius: var(--button-radius);
    background: var(--card-bg);
    color: var(--text-primary);
    font-size: 14px;
    transition: var(--transition-default);
    
    &:focus {
      outline: none;
      border-color: var(--accent-primary);
      box-shadow: 0 0 0 3px rgba(51, 156, 255, 0.15);
    }
    
    &::placeholder {
      color: var(--text-secondary);
    }
  }

  .clear-btn {
    position: absolute;
    right: 8px;
    background: transparent;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      color: var(--text-primary);
    }
  }
}

.header-right {
  display: flex;
  align-items: center;
}

.mobile-main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 72px; // Space for bottom nav
}

.products-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch; // Smooth scrolling on iOS
}

.navigation-drawer {
  min-width: 320px;
  width: 100%;
  height: 100vh;
  background: var(--card-bg);
}

.open-table-banner {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  color: white;
  padding: 12px 16px;
  margin: 12px;
  border-radius: var(--button-radius);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
  cursor: pointer;
  transition: transform 0.2s;

  &:active {
    transform: scale(0.98);
  }
}

.change-notification-banner {
  position: fixed;
  right: 12px;
  bottom: 78px;
  z-index: 1200;
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  color: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(var(--accent-electric-blue-rgb), 0.35);
  padding: 10px 14px;
  min-width: 190px;
}

.change-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.change-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.change-label {
  font-size: 11px;
  letter-spacing: 1px;
  font-weight: 700;
}

.invoice-label {
  font-size: 10px;
  opacity: 0.9;
}

.change-amount {
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
}

.dismiss-btn {
  margin-top: 4px;
  border: none;
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--accent-electric-blue);
  font-weight: 700;
  padding: 4px 12px;
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: all 0.25s ease;
}

.slide-down-enter, .slide-down-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
